import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

// Define the maximum number of events to store in history
const MAX_EVENTS = 100;

// --- Interfaces ---
interface LogEntry {
  timestamp: string;
  type: string;
  message: string;
  details: any;
}

interface LoggerContextType {
  logHistory: LogEntry[];
  // Expose recordEvent for other components to log manually
  recordEvent: (type: string, message: string, details?: any) => void;
}

// 1. Create the Context object
const LoggerContext = createContext<LoggerContextType | undefined>(undefined);

// Define original console methods globally once
const originalConsole = {
  error: console.error,
  warn: console.warn,
  //info: console.info,
};

let isLoggingInternal = false;
// 2. Create the Provider component (The persistent wrapper)
export const LoggerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [logHistory, setLogHistory] = useState<LogEntry[]>([]);

  // The core function for immutable state update (with setTimeout fix for "setState in render" warning)
  const recordEvent = useCallback((type: string, message: string, details = {}) => {
    const newLogEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      type,
      message,
      details,
    };

    // Use setTimeout(0) to prevent the state update from running during a render phase
    setTimeout(() => {
      setLogHistory(prevHistory => {
        let updatedHistory = [...prevHistory, newLogEntry];

        // Apply MAX_EVENTS limit immutably by slicing the oldest element
        if (updatedHistory.length > MAX_EVENTS) {
          updatedHistory = updatedHistory.slice(1);
        }
        return updatedHistory;
      });
    }, 0);
  }, []); // recordEvent is stable due to useCallback

  // 3. useEffect to set up the console overrides and window event handlers
  useEffect(() => {
    // --- Store original handlers for cleanup ---
    const originalOnError = window.onerror;
    const originalOnUnhandledRejection = window.onunhandledrejection;

    // Save original console methods before overriding them for cleanup
    const originalConsoleMethods = {
      error: originalConsole.error,
      warn: originalConsole.warn,
      //: originalConsole.info,
    };

    // --- SETUP: Override Console Methods (Catches intentional logs) ---
    //['log', 'error', 'warn', 'info'].forEach((method: any) => {
    ['error', 'warn'].forEach((method: any) => {
      (console as any)[method] = (...args: any[]) => {
        if (!isLoggingInternal) {
          isLoggingInternal = true; // Set the flag

          try {
            // 2. Record the Event (This triggers the state update)
            const message = args.map(arg =>
              typeof arg === 'object' && arg !== null ? JSON.stringify(arg) : String(arg)
            ).join(' ');
            recordEvent(method, message);
          } finally {
            isLoggingInternal = false; // Reset the flag, regardless of success/fail
          }
        }
        //(originalConsole as any)[method](...args);
      };
    });


    // --- SETUP: window.onerror Handler (Catches sync errors) ---
    const handleError = (message: any, source: any, lineno: any, colno: any, error: any) => {
      const errorDetails = {
        source,
        lineno,
        colno,
        stack: error ? error.stack : 'N/A'
      };
      recordEvent('uncaught_error', String(message), errorDetails);

      // Call the previous handler if it exists
      if (originalOnError) {
        originalOnError(message, source, lineno, colno, error);
      }
      return false; // Prevents default browser error handling
    };
    window.onerror = handleError;


    // --- SETUP: window.onunhandledrejection Handler (Catches async errors) ---
    const handleRejection = (event: PromiseRejectionEvent) => {
      let reason = event.reason;
      let message = 'Unhandled Promise Rejection';
      let stack = 'N/A';

      if (reason instanceof Error) {
        message = reason.message;
        stack = reason.stack || "N/A";
      } else if (typeof reason === 'string') {
        message = reason;
      }

      recordEvent('unhandled_rejection', message, { stack });

      // Call the previous handler if it exists
      if (originalOnUnhandledRejection) {
        originalOnUnhandledRejection.call(window, event);
      }

      // Prevent the default console output for this rejection
      event.preventDefault();
    };
    window.onunhandledrejection = handleRejection;


    // --- CLEANUP (Restores global state on unmount) ---
    return () => {
      // Restore original console methods
      //['log', 'error', 'warn', 'info'].forEach((method: string) => {
      ['error', 'warn'].forEach((method: string) => {
        (console as any)[method] = (originalConsoleMethods as any)[method];
      });

      // Restore original window handlers
      window.onerror = originalOnError;
      window.onunhandledrejection = originalOnUnhandledRejection;
    };

  }, [recordEvent]); // recordEvent is the only dependency (it's wrapped in useCallback)

  const value = { logHistory, recordEvent };

  return (
    <LoggerContext.Provider value={value}>
      {children}
    </LoggerContext.Provider>
  );
};

// 4. Create a custom Hook for easy consumption
export const useLogger = () => {
  const context = useContext(LoggerContext);
  if (context === undefined) {
    throw new Error('useLogger must be used within a LoggerProvider');
  }
  return context;
};